import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { Box, IconButton, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    getLoadedCatalogId,
    getLocalCategoriesList,
} from 'entities/Category/model/selectors/categorySelectors';
import { fetchCategories } from 'entities/Category/model/services/fetchCategories/fetchCategories';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { deriveChangeCatalogItemCategoryState } from '../../lib/deriveChangeCatalogItemCategoryState';
import { resolveSelectionToItemInput } from '../../lib/resolveSelectionToItemInput';
import { ChangeCatalogItemCategoryDialog } from '../ChangeCatalogItemCategoryDialog/ChangeCatalogItemCategoryDialog';

import type {
    CatalogCategoryChangeFollowUp,
    CatalogItemCategoryChangeInput,
} from '../../lib/types';
import type { MappedCatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';

export type ChangeCatalogItemCategoryButtonVisualVariant =
    | 'propertyCatalogToolbar'
    | 'orderRow'
    | 'icon';

export interface ChangeCatalogItemCategoryButtonProps {
    // This can power proper items (order list or item detail for example)
    items?: CatalogItemCategoryChangeInput[];
    // This powers selections from the Property Catalog bulk actions.
    selectedItems?: Record<string, Partial<MappedCatalogItem>>;
    catalogId?: string;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
    visualVariant?: ChangeCatalogItemCategoryButtonVisualVariant;
    'data-testclass'?: string;
}

export const ChangeCatalogItemCategoryButton = memo(
    ({
        items: itemsProp,
        selectedItems,
        catalogId,
        onAfterCatalogCategoryChange,
        visualVariant = 'propertyCatalogToolbar',
        'data-testclass': dataTestClass,
    }: ChangeCatalogItemCategoryButtonProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const localCategories = useSelector(getLocalCategoriesList);
        const loadedCatalogId = useSelector(getLoadedCatalogId);
        const [dialogOpen, setDialogOpen] = useState(false);
        const [categoriesLoading, setCategoriesLoading] = useState(false);

        const catalogItems = useMemo((): CatalogItemCategoryChangeInput[] => {
            if (itemsProp?.length) {
                return itemsProp;
            }
            return resolveSelectionToItemInput(selectedItems);
        }, [itemsProp, selectedItems]);

        const changeCatalogState = useMemo(
            () =>
                deriveChangeCatalogItemCategoryState(
                    catalogItems,
                    localCategories,
                    t,
                ),
            [catalogItems, localCategories, t],
        );

        if (!changeCatalogState) {
            return null;
        }

        const { useAddCategoryTitle } = changeCatalogState;
        const actionLabel = useAddCategoryTitle
            ? t('Add Category')
            : t('Edit Category');
        const isToolbar = visualVariant === 'propertyCatalogToolbar';
        const isIconOnly = visualVariant === 'icon';

        const openDialog = async (): Promise<void> => {
            if (!catalogId || loadedCatalogId === catalogId) {
                setDialogOpen(true);
                return;
            }

            setCategoriesLoading(true);
            try {
                await dispatch(fetchCategories(catalogId)).unwrap();
                setDialogOpen(true);
            } catch {
                return;
            } finally {
                setCategoriesLoading(false);
            }
        };

        const icon = useAddCategoryTitle ? (
            <CreateNewFolderIcon
                sx={{
                    fontSize: '2rem',
                    width: '20px',
                    height: '20px',
                }}
            />
        ) : (
            <DriveFileMoveIcon
                sx={{
                    fontSize: '2rem',
                    width: '20px',
                    height: '20px',
                }}
            />
        );

        const dialog = dialogOpen ? (
            <ChangeCatalogItemCategoryDialog
                changeCatalogState={changeCatalogState}
                catalogId={catalogId}
                onAfterCatalogCategoryChange={onAfterCatalogCategoryChange}
                onClose={() => setDialogOpen(false)}
            />
        ) : null;

        if (isIconOnly) {
            return (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 0,
                            height: '2rem',
                        }}
                    >
                        <DynamicTooltip
                            content={actionLabel}
                            containerStyle={{
                                display: 'inline-flex',
                                lineHeight: 0,
                            }}
                        >
                            <IconButton
                                data-testclass={dataTestClass}
                                aria-label={actionLabel}
                                disabled={categoriesLoading}
                                onKeyDown={(event) => event.stopPropagation()}
                                onClick={() => {
                                    void openDialog();
                                }}
                                sx={{
                                    color: 'primary.main',
                                    width: '2.4rem',
                                    height: '2.4rem',
                                    minWidth: '2.4rem',
                                    padding: '0.2rem',
                                    marginTop: '-0.2rem',
                                    marginBottom: '-0.2rem',
                                    boxSizing: 'border-box',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: '#E8EEF6',
                                    },
                                }}
                            >
                                {icon}
                            </IconButton>
                        </DynamicTooltip>
                    </Box>
                    {dialog}
                </>
            );
        }

        return (
            <>
                <Box
                    data-testclass={dataTestClass}
                    display="flex"
                    alignItems="center"
                    aria-disabled={categoriesLoading}
                    onClick={() => {
                        if (!categoriesLoading) void openDialog();
                    }}
                    sx={
                        isToolbar
                            ? (theme) => ({
                                  cursor: 'pointer',
                                  gap: '4px',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  '&:hover': {
                                      backgroundColor: '#E8EEF6',
                                  },
                                  '& .MuiSvgIcon-root': {
                                      color: theme.palette.primary.main,
                                  },
                              })
                            : {
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  height: '32px',
                                  minHeight: '32px',
                                  boxSizing: 'border-box',
                                  padding: '0 10px',
                                  borderRadius: '4px',
                                  '&:hover': {
                                      backgroundColor: '#E8EEF6',
                                  },
                                  '& .MuiSvgIcon-root': {
                                      color: '#3F6EB7',
                                  },
                              }
                    }
                >
                    {icon}
                    <Typography
                        color={isToolbar ? 'primary' : undefined}
                        sx={
                            isToolbar
                                ? {
                                      fontSize: '1.4rem',
                                      fontWeight: 600,
                                      lineHeight: '2.4rem',
                                  }
                                : {
                                      color: '#3F6EB7',
                                      fontSize: '1.3rem',
                                      fontWeight: 500,
                                      lineHeight: '2rem',
                                  }
                        }
                        variant="subtitle2"
                    >
                        {actionLabel}
                    </Typography>
                </Box>
                {dialog}
            </>
        );
    },
);
